import * as React from 'react';
import { logger } from '../lib/loggerService';
import { FaTimes } from 'react-icons/fa';
import axios from 'axios';
import { env } from '../env';

interface ContactFormProps {
  onClose: () => void;
  isAgency?: boolean;
}

interface ContactResponse {
  success: boolean;
  message: string;
  error?: string;
}

const ContactForm: React.FC<ContactFormProps> = ({ onClose, isAgency = false }) => {
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [message, setMessage] = React.useState(isAgency ? 'I\'m interested in white-label reports & prospecting tools for my Amazon agency or I want to use your tool with my name.' : '');
  const [company, setCompany] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [error, setError] = React.useState('');
  const [previewUrl, setPreviewUrl] = React.useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name || !email || !message) {
      setError('Please fill out all required fields');
      return;
    }
    
    try {
      setIsSubmitting(true);
      setError('');
      
      const response = await axios.post(`${env.API_BASE_URL}/api/contact`, {
        name,
        email,
        company,
        message,
      });

      const data = response.data as ContactResponse;
      logger.log('Success:', data);
      
      if (data.success) {
        setIsSubmitted(true);
        setName('');
        setEmail('');
        setCompany('');
        setMessage('');
      } else {
        setError(data.error || 'Something went wrong. Please try again.');
      }
      
      setIsSubmitting(false);
    } catch (err) {
      logger.error('Error sending contact form:', err);
      setError('There was a problem sending your message. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      backdropFilter: 'blur(5px)',
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
        width: '90%',
        maxWidth: '700px',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative',
        padding: '35px',
      }}>
        {/* Close button */}
        <button 
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '15px',
            right: '15px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: '1.2rem',
            color: '#6b7280',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            transition: 'background-color 0.2s',
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = '#f3f4f6';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <FaTimes />
        </button>

        <h2 style={{ 
          fontSize: '1.75rem', 
          color: '#1f2937', 
          marginBottom: '25px',
          fontWeight: '700'
        }}>
          {isAgency ? 'Amazon Agency Solutions' : 'Contact Us'}
        </h2>

        {isAgency && (
          <div style={{
            backgroundColor: '#f0f9ff',
            borderRadius: '8px',
            padding: '18px',
            marginBottom: '24px',
            border: '1px solid #bae6fd',
            boxShadow: '0 2px 5px rgba(0, 0, 0, 0.05)',
          }}>
            <h3 style={{ 
              margin: '0 0 10px 0', 
              color: '#0369a1',
              fontSize: '1.2rem',
              fontWeight: '600',
            }}>
              ATTN: AMAZON AGENCIES
            </h3>
            <p style={{ 
              margin: '0', 
              color: '#0c4a6e',
              lineHeight: '1.5',
              fontSize: '1rem',
            }}>
              White-label reports & prospecting tools for your Amazon seller clients
            </p>
          </div>
        )}

        {isSubmitted ? (
          <div style={{
            marginTop: '20px',
            padding: '20px',
            backgroundColor: '#f0fdf4',
            borderRadius: '8px',
            border: '1px solid #86efac',
          }}>
            <h3 style={{ 
              fontSize: '1.25rem', 
              color: '#166534', 
              marginBottom: '10px',
              fontWeight: '600'
            }}>
              Thank you for your message!
            </h3>
            <p style={{ color: '#166534' }}>
              We've received your inquiry and will get back to you as soon as possible.
            </p>
            {previewUrl && window.location.hostname === 'localhost' && (
              <div style={{ marginTop: '15px' }}>
                <p style={{ fontSize: '0.95rem', color: '#4b5563', marginBottom: '10px', fontWeight: '500' }}>
                  Development Mode:
                </p>
                <p style={{ fontSize: '0.95rem', color: '#4b5563', marginBottom: '10px' }}>
                  Your message has been captured for testing.
                </p>
                <a 
                  href={previewUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-block',
                    padding: '10px 20px',
                    backgroundColor: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    transition: 'background-color 0.2s',
                    fontSize: '0.95rem',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#059669';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#10b981';
                  }}
                >
                  View Test Email
                </a>
              </div>
            )}
            <button
              onClick={onClose}
              style={{
                marginTop: '15px',
                padding: '10px 20px',
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                transition: 'background-color 0.2s',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#059669';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = '#10b981';
              }}
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '20px' }}>
              <label 
                htmlFor="name"
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '0.95rem',
                }}
              >
                Name *
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  fontSize: '1rem',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#4338ca';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.2)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#d1d5db';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label 
                htmlFor="email"
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '0.95rem',
                }}
              >
                Email *
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  fontSize: '1rem',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#4338ca';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.2)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#d1d5db';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label 
                htmlFor="company"
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '0.95rem',
                }}
              >
                {isAgency ? 'Business Name *' : 'Company'}
              </label>
              <input
                id="company"
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                required={isAgency}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  fontSize: '1rem',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#4338ca';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.2)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#d1d5db';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>

            <div style={{ marginBottom: '25px' }}>
              <label 
                htmlFor="message"
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '0.95rem',
                }}
              >
                Message *
              </label>
              <textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                rows={5}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  fontSize: '1rem',
                  resize: 'vertical',
                  minHeight: '120px',
                  fontFamily: 'inherit',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#4338ca';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.2)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#d1d5db';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>

            {error && (
              <div style={{
                backgroundColor: '#fee2e2',
                color: '#b91c1c',
                padding: '12px 16px',
                borderRadius: '8px',
                marginBottom: '20px',
                fontSize: '0.95rem',
                border: '1px solid #fecaca',
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  backgroundColor: 'transparent',
                  color: '#4b5563',
                  border: '1px solid #d1d5db',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  marginRight: '10px',
                  transition: 'all 0.2s ease',
                  fontSize: '0.95rem',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  backgroundColor: '#4338ca',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  fontSize: '0.95rem',
                  opacity: isSubmitting ? 0.7 : 1,
                  boxShadow: '0 4px 6px rgba(67, 56, 202, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onMouseOver={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.backgroundColor = '#4f46e5';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseOut={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.backgroundColor = '#4338ca';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }
                }}
              >
                {isSubmitting ? (
                  <>
                    <span style={{ 
                      display: 'inline-block',
                      width: '18px',
                      height: '18px',
                      border: '3px solid rgba(255, 255, 255, 0.3)',
                      borderTopColor: 'white',
                      borderRadius: '50%',
                      marginRight: '8px',
                      animation: 'spin 1s linear infinite',
                    }}></span>
                    Sending...
                  </>
                ) : 'Send Message'}
              </button>
            </div>
          </form>
        )}
      </div>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideIn {
          from { transform: translateX(20px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default ContactForm;
